// Abgaben an Kunden: Formen, die Server, Management und Kundenseite teilen.

export interface DeliveryVideoRow {
  id: string;
  title: string;
  fileName: string;
  mime: string;
  size: number;
  received: number;
  status: "uploading" | "transfer" | "ready" | "error";
  /** Fehler bei der Übertragung in die Nextcloud */
  error: string;
  storage: "local" | "nextcloud";
  duration: number;
  width: number;
  height: number;
  version: number;
  /** Social-Media-Vorschau, die der Kunde einschalten kann */
  platforms: Platform[];
  /** Beitragstext für die Vorschau (für alle Plattformen) */
  caption: string;
  /** eigener Text pro Plattform; fehlt einer, gilt caption */
  captions: Partial<Record<Platform, string>>;
  approvedAt: string | null;
  approvedBy: string;
  /** Adressen relativ zur Seite */
  src: string;
  poster: string;
  download: string;
}
export interface DeliveryCommentRow {
  id: string;
  videoId: string;
  version: number;
  at: number | null;
  author: string;
  text: string;
  done: boolean;
  createdAt: string;
}
export interface DeliveryEventRow {
  id: string;
  kind: "open" | "play" | "download" | "approve" | "comment" | "request";
  videoId: string | null;
  detail: string;
  createdAt: string;
}
/** final = Abgabe der fertigen Videos | preview = Vorschau: Einblick in einen Zwischenstand (v1, Sample) | photos = Fotos: Galerie zum Ansehen und Herunterladen */
export type DeliveryKind = "final" | "preview" | "photos";
export const DELIVERY_KINDS = ["final", "preview", "photos"] as const;

export interface DeliveryListItem {
  id: string;
  kind: DeliveryKind;
  title: string;
  clientId: string | null;
  projectId: string | null;
  active: boolean;
  expired: boolean;
  videos: number;
  approved: number;
  openComments: number;
  poster: string;
  firstOpenedAt: string | null;
  /** archiviert seit (Link ist dann aus) */
  archivedAt: string | null;
  /** wird an diesem Tag endgültig gelöscht */
  purgeAt: string | null;
  /** offene Bitte des Kunden, die Abgabe nochmals freizugeben */
  request: ReopenRequest | null;
  lastOpenedAt: string | null;
  createdAt: string;
}
export interface DeliveryFull extends DeliveryListItem {
  token: string;
  message: string;
  /** Code im Klartext für David (leer = ohne Code) */
  code: string;
  /** Code davor zum Zurückholen: null = keiner, "" = vorher ohne Code */
  prevCode: string | null;
  allowDownload: boolean;
  showSafeZone: boolean;
  expiresAt: string | null;
  /** fertiger Link für den Kunden */
  link: string;
  /** Ordner in der Nextcloud (leer = lokal gespeichert) */
  folder: string;
  videoRows: DeliveryVideoRow[];
  comments: DeliveryCommentRow[];
  events: DeliveryEventRow[];
}

export interface ReopenRequest {
  name: string;
  /** E-Mail oder Telefon, falls angegeben */
  contact: string;
  message: string;
  at: string;
}

export type Platform = "instagram" | "tiktok" | "linkedin";
export const PLATFORMS: Record<Platform, { label: string; vertical: boolean }> = {
  instagram: { label: "Instagram", vertical: true },
  tiktok: { label: "TikTok", vertical: true },
  linkedin: { label: "LinkedIn", vertical: false },
};
/** Text für eine Plattform: eigener, sonst der gemeinsame, sonst der Titel */
export const captionFor = (v: Pick<DeliveryVideoRow, "caption" | "captions" | "title">, p: Platform) => v.captions[p]?.trim() || v.caption || v.title;
export const parseCaptions = (s: string): Partial<Record<Platform, string>> => {
  try {
    const o = JSON.parse(s) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(o).filter(([k, x]) => k in PLATFORMS && typeof x === "string" && x.trim())) as Partial<Record<Platform, string>>;
  } catch {
    return {};
  }
};
export const parsePlatforms = (s: string): Platform[] => s.split(",").filter((x): x is Platform => x in PLATFORMS);

/** Profil, unter dem die Social-Media-Vorschau erscheint (Einstellungen) */
export interface SocialBrand {
  name: string;
  handle: string;
  /** Zeile unter dem Namen bei LinkedIn */
  line: string;
}
export const DEFAULT_BRAND: SocialBrand = { name: "Social Solve", handle: "socialsolve.ch", line: "Videoproduktion für Social Media" };

/** Echte Angaben für den Zeitstrahl «Der Weg zum Video» (fehlt etwas, bleibt die Station ohne Detail) */
export interface ShareStory {
  /** Aufnahmen im verknüpften Drehbuch */
  scenes: number;
  /** Drehtag aus dem Drehbuch, wie dort eingetragen */
  shootDate: string;
}

/** Video, wie es die Kundenseite sieht: ohne Dateinamen, Speicherort oder interne Zustände */
export type ShareVideo = Pick<DeliveryVideoRow, "id" | "title" | "size" | "duration" | "width" | "height" | "version" | "platforms" | "caption" | "captions" | "approvedAt" | "approvedBy" | "src" | "poster" | "download">;

/** Was die Kundenseite sieht */
export interface SharePublic {
  kind: DeliveryKind;
  brand: SocialBrand;
  /** Absender für das Dankeschön (Firmeninhaber) */
  from: string;
  story: ShareStory;
  title: string;
  message: string;
  client: string;
  createdAt: string;
  allowDownload: boolean;
  /** Knopf «Sicherheitsbereich» anzeigen (stellt David pro Abgabe ein) */
  showSafeZone: boolean;
  videos: ShareVideo[];
  comments: DeliveryCommentRow[];
}

/** Grösse einer Upload-Teildatei: unter dem 6-MB-Limit von nginx */
/** Archivierte Abgaben werden nach so vielen Tagen endgültig gelöscht */
export const ARCHIVE_DAYS = 30;

export const CHUNK = 4 * 1024 * 1024;
export const MAX_VIDEO = 4 * 1024 * 1024 * 1024;
export const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
export const MAX_PHOTO = 200 * 1024 * 1024;
export const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
/** Lange Kante der Ansicht in der Galerie (das Original bleibt für den Download) */
export const PHOTO_VIEW_PX = 2000;

export const fmtTime = (s: number) => {
  const t = Math.max(0, Math.floor(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};
export const fmtBytes = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${Math.round(n / 1e6)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
